import {
  autoUpdate,
  flip,
  offset,
  Placement,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
} from '@floating-ui/react';
import { useState } from 'react';

interface UseTooltipProps {
  position?: Placement;
}

/**
 * 툴팁의 상태와 기능을 관리
 * Floating UI를 사용하여 구현되었으며, 부모 요소의 overflow 속성에 관계없이 정확한 위치에 툴팁 표시
 *
 * @param {Placement} options.position - 툴팁 위치 (기본값: bottom)
 */
export const useTooltip = ({ position = 'bottom' }: UseTooltipProps) => {
  // 툴팁 표시 상태
  const [isOpen, setIsOpen] = useState(false);

  // Floating UI의 기본 훅
  const { x, y, refs, placement, context } = useFloating({
    placement: position, // 툴팁 위치
    open: isOpen, // 표시 상태
    onOpenChange: setIsOpen, // 상태 변경 핸들러
    strategy: 'fixed', // 부모 overflow 속성 무시
    middleware: [
      offset(8), // 기준 요소로부터 8px 간격 띄우기
      flip(), // 툴팁이 화면 밖 나갔을 때 방향 전환
      shift({ padding: 8 }), // 화면 밖으로 나가지 않도록 이동 (8px 여백 유지)
    ],
    whileElementsMounted: autoUpdate, // 요소가 마운트된 상태에서 위치 자동 업데이트
  });

  // 마우스 호버 이벤트 처리 (move: false는 요소 위에서 마우스 이동 시 재계산 방지)
  const hover = useHover(context, { move: false });
  // 포커스 이벤트 처리
  const focus = useFocus(context);
  // 툴팁 닫기 이벤트 처리
  const dismiss = useDismiss(context);

  // 모든 이벤트 핸들러 통합
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
  ]);

  // 툴팁 제어에 필요한 값과 함수 반환
  return {
    isOpen,
    x,
    y,
    refs,
    placement, // 실제 배치된 위치
    getReferenceProps,
    getFloatingProps,
  };
};
